/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag
 */
import React, { lazy, Suspense } from 'react';

import { FormattedMessage } from 'react-intl-next';

import AvatarGroup from '@atlaskit/avatar-group';
import { jsx } from '@atlaskit/css';
import Link from '@atlaskit/link';
import { Inline, Stack, Text } from '@atlaskit/primitives/compiled';

import type {
	InvitedAppShortcutAllowedApsKey,
	InvitedAppShortcutCollaborator,
} from '../../../../common/types';
import { messages } from '../../../../common/ui/invited-app-shortcuts/messages';
import { getAppKeyByApsKey } from '../../../../common/utils/invited-app-shortcuts-utils';
import type { UseInvitedAppShortcutsResult } from '../../../../services/invited-app-shortcuts';
import { useInvitedAppAnalytics } from '../../../../services/invited-app-shortcuts/invited-app-analytics';

import { InvitedAppCollaboratorsTooltip } from './invited-app-collaborators-tooltip';

// Lazy load the InvitedAppPopup component
const InvitedAppPopup = lazy(() =>
	import('./invited-app-popup').then((module) => ({
		default: module.InvitedAppPopup,
	})),
);

export interface LazyInvitedAppPopupProps {
	currentAppCollaborators: InvitedAppShortcutCollaborator[];
	currentApsKey: InvitedAppShortcutAllowedApsKey;
	currentShortcutLink: string;
	onMouseEnterPopup: () => void;
	onMouseLeavePopup: () => void;
	resolverResult?: UseInvitedAppShortcutsResult;
	trigger: React.MutableRefObject<HTMLAnchorElement | null>;
}

export const LazyInvitedAppPopup = ({
	currentAppCollaborators,
	currentApsKey,
	currentShortcutLink,
	onMouseEnterPopup,
	onMouseLeavePopup,
	resolverResult,
	trigger,
}: LazyInvitedAppPopupProps) => {
	const { fireLinkClickedAcceptInvite } = useInvitedAppAnalytics();

	return (
		<Suspense fallback={null}>
			<InvitedAppPopup
				trigger={trigger}
				onMouseEnterPopup={onMouseEnterPopup}
				onMouseLeavePopup={onMouseLeavePopup}
				currentApsKey={currentApsKey}
				currentAppCollaborators={currentAppCollaborators}
			>
				<Inline>
					<Inline grow="fill">
						<Stack alignBlock="start" alignInline="start">
							<Text>
								{currentAppCollaborators.length > 0 ? (
									<FormattedMessage {...messages.popupMainTextWithCollaborators} />
								) : (
									<FormattedMessage {...messages.popupMainTextWithoutCollaborators} />
								)}
							</Text>
							<Link
								href={currentShortcutLink}
								target="_blank"
								rel="noopener noreferrer"
								onClick={() => {
									const app = getAppKeyByApsKey(currentApsKey);
									fireLinkClickedAcceptInvite({ app });
									resolverResult?.registerActivation(app);
								}}
							>
								<FormattedMessage {...messages.popupAcceptInviteButton} />
							</Link>
						</Stack>
					</Inline>
					{currentAppCollaborators.length > 0 && currentApsKey && (
						<Inline grow="hug" key={currentApsKey}>
							<AvatarGroup
								data={currentAppCollaborators.map((collaborator) => ({
									...collaborator,
									appearance: 'circle',
									size: 'medium',
								}))}
								maxCount={2}
								overrides={{
									MoreIndicator: {
										render: (Component, props) => (
											<InvitedAppCollaboratorsTooltip
												currentApsKey={currentApsKey}
												currentAppCollaborators={currentAppCollaborators}
											>
												<Component {...props} isActive={false} onClick={() => {}} />
											</InvitedAppCollaboratorsTooltip>
										),
									},
								}}
							/>
						</Inline>
					)}
				</Inline>
			</InvitedAppPopup>
		</Suspense>
	);
};
